import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { color } from '../../app/theme';
import { ImagemapTemplateType, SnackBarType } from '../../app/types';
import {
  scrollToWithAnimation,
  validateFileName,
  validateHttpsAndHttpPath,
  validateLinePath,
  validateNo8Path,
} from '../../app/utils';
import ImagemapTemplate1 from '../../assets/images/templates/ImagemapTemplate1';
import ImagemapTemplate2 from '../../assets/images/templates/ImagemapTemplate2';
import ImagemapTemplate3 from '../../assets/images/templates/ImagemapTemplate3';
import ImagemapTemplate4 from '../../assets/images/templates/ImagemapTemplate4';
import ImagemapTemplate5 from '../../assets/images/templates/ImagemapTemplate5';
import { enqueueSnackbar } from '../../features/notifierSlice';
import FileService from '../../services/FileService';
import Button from './Button';
import DialogText from './DialogText';
import Dropdown from './Dropdown';
import TextFieldCardCounter from './TextFieldCardCounter';
import MenuContentBox from './imagemap/MenuContentBox';

const customerMessageTemplateType = 'ImagemapTemplate5';

let timer = null;
const screenSize = 320;
const IMAGE_MAX_SIZE = 2 * 1000 * 1000; // 2MB

const useStyles = makeStyles(
  theme => ({
    root: {},
    title: {
      color: color.$3,
      fontSize: 15,
      fontWeight: 'normal',
      lineHeight: 1,
      margin: '0 0 12px',
      '& > a': {
        ...theme.link.primaryGreen.default,
        display: 'inline-block',
        marginLeft: 8,
        fontSize: 13,
      },
    },
    messageTemplateContainer: {
      margin: '0 0 32px',
    },
    messageTemplate: {
      display: 'flex',
      maegin: '0 0 32px',
    },
    messageTemplateItem: {
      cursor: 'pointer',
      margin: '0 12px 0 0',
      '& > svg': {
        display: 'block',
      },
    },
    bgImageContainer: {},
    bgImageContent: {
      display: 'flex',
      alignItems: 'flex-end',
      margin: '0 0 32px 0',
    },
    bgImage: {
      position: 'relative',
      backgroundColor: color.$8,
      '& > img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      cursor: 'crosshair',
      border: '1px solid transparent',
    },
    'bgImage--error': {
      borderColor: color.$17,
    },
    actionBox: {
      position: 'absolute',
      zIndex: 1,
      border: `1px solid #2ca6e0`,
      backgroundColor: 'rgba(44, 166, 224, 0.4)',
    },
    actionBoxNumber: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateX(-50%) translateY(-50%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 24,
      height: 24,
      fontSize: 13,
      lineHeight: 1,
      color: color.$3,
      backgroundColor: color.$7,
      borderRadius: '50%',
    },
    bgImageUpload: {
      width: 560,
      fontSize: 13,
      lineHeight: '19px',
      color: color.$11,
      margin: '0 0 0 16px',
    },
    'bgImageUpload-errorMessage': {
      color: color.$17,
      fontSize: 13,
      lineHeight: '19px',
      margin: 0,
    },
    actionSettingContainer: {
      margin: '0 0 24px 0',
      '&:last-child': {
        marginBottom: 0,
      },
      '& > label': {
        display: 'block',
        fontSize: 15,
        lineHeight: 1,
        margin: '0 0 12px 0',
      },
    },
    actionSetting: {
      display: 'flex',
      alignItems: 'center',
    },
    dropdown: {
      flex: '0 0 240px',
      marginRight: 8,
    },
    'actionSetting-input': {
      marginRight: 8,
      minWidth: 390,
      paddingLeft: 24,
    },
    newBox: {
      display: 'none',
      position: 'absolute',
      border: 'solid 1px #2ca6e0',
      backgroundColor: 'rgba(44, 166, 224, 0.4)',
      top: 0,
      left: 0,
    },
  }),
  {
    name: 'ImagemapDialog',
  },
);

const toPercentage = (molecular, denominator) => {
  return `${((molecular / denominator) * 100).toFixed(2)}%`;
};

const validAction = (action, tempIsModified) => {
  let isValid = true;

  if (tempIsModified) {
    if (action.type !== 'location') {
      if (!action.data) {
        isValid = false;
      } else {
        if (action.type === 'postback') {
          if (action.data.length > 20) {
            isValid = false;
          }
        }
        if (action.type === 'url') {
          isValid =
            validateHttpsAndHttpPath(action.data) ||
            validateNo8Path(action.data) ||
            validateLinePath(action.data);
        }
      }
    }
  }
  return isValid;
};

function validateCardData(data, uploadImageError) {
  if (!data.imageUrl) return false;
  for (let i = 0; i < data.buttons.length; i += 1) {
    const isVald = validAction(data.buttons[i], true);
    if (!isVald) return false;
  }
  return true && !uploadImageError;
}

function ImagemapDialog({ data, onCancel, onSubmit }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const imageRef = useRef(null);
  const newBoxRef = useRef(null);
  const bgImageRef = useRef(null);
  const contentRef = useRef(null);
  const [localData, setLocalData] = useState(data);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [uploadImageError, setUploadImageError] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [selectedContentIndex, setSelectedContentIndex] = useState(null);

  const dropdownList = [
    {
      text: t('templates.imagemap-edit.item-dropdown-type.postback'),
      value: 'postback',
    },
    {
      text: t('templates.imagemap-edit.item-dropdown-type.url'),
      value: 'url',
    },
    // { text: t('templates.button-type.location'), value: 'location' },
  ];

  const maxAreaNum = 20;
  const maxWidth = screenSize;
  const [maxHeight, setMaxHeight] = useState(
    data.size.height * (screenSize / data.size.width),
  );
  const minWidth = 30;
  const minHeight = 30;

  const getPosition = element => {
    let x = 0;
    let y = 0;
    // 搭配上面的示意圖可比較輕鬆理解為何要這麼計算
    while (element) {
      x += element.offsetLeft - element.scrollLeft + element.clientLeft;
      y += element.offsetTop - element.scrollLeft + element.clientTop;
      // eslint-disable-next-line no-param-reassign
      element = element.offsetParent;
    }

    return { x, y };
  };

  const onMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    if (localData.buttons.length < maxAreaNum) {
      const box = newBoxRef.current;

      let { clientX: startX, clientY: startY } = e;
      let width = 0;
      let height = 0;
      let left = startX - getPosition(imageRef.current).x;
      let top =
        startY - getPosition(imageRef.current).y + contentRef.current.scrollTop;

      box.style.display = 'inline-block';
      box.style.top = `${top}px`;
      box.style.left = `${left}px`;

      const onMove = eM => {
        eM.stopImmediatePropagation();

        const { clientX, clientY } = eM;
        width += clientX - startX;
        height += clientY - startY;

        if (width < 0) {
          left += clientX - startX;
        }

        if (height < 0) {
          top += clientY - startY;
        }
        if (width > maxWidth) width = maxWidth;
        if (height > maxHeight) height = maxHeight;
        if (left <= 0) left = 0;
        if (left >= maxWidth - width) left = maxWidth - width;
        if (top <= 0) top = 0;
        if (top >= maxHeight - height) top = maxHeight - height;

        box.style.top = `${top}px`;
        box.style.left = `${left}px`;
        box.style.width = `${Math.abs(width)}px`;
        box.style.height = `${Math.abs(height)}px`;

        startX = clientX;
        startY = clientY;
      };
      const onUp = () => {
        width = Math.abs(width);
        height = Math.abs(height);
        if (width < minWidth) width = minWidth;
        if (height < minHeight) height = minHeight;

        setLocalData({
          ...localData,
          buttons: [
            ...localData.buttons,
            {
              title: '',
              type: 'postback',
              data: '',
              tags: [],
              x: toPercentage(left, maxWidth),
              y: toPercentage(top, maxHeight),
              width: toPercentage(width, maxWidth),
              height: toPercentage(height, maxHeight),
            },
          ],
        });

        setSelectedContentIndex(localData.buttons.length);
        box.style.display = 'none';
        box.style.top = `0px`;
        box.style.left = `0px`;
        box.style.width = `0px`;
        box.style.height = `0px`;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('mouseleave', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('mouseleave', onUp);
    } else {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: t('templates.imagemap-edit.alert'),
        }),
      );
    }
  };

  const removeAreaByIndex = index => {
    setLocalData({
      ...localData,
      buttons: localData.buttons.filter((d, i) => i !== index),
    });
  };

  const setBounds = ({ index, bounds }) => {
    setLocalData({
      ...localData,
      buttons: localData.buttons.map((btn, actionIndex) => {
        if (index === actionIndex) {
          return {
            ...btn,
            x: toPercentage(bounds?.x, maxWidth),
            y: toPercentage(bounds?.y, maxHeight),
            width: toPercentage(bounds?.width, maxWidth),
            height: toPercentage(bounds?.height, maxHeight),
          };
        }
        return btn;
      }),
    });
  };

  function handleChangeMessageTemplateType(type) {
    let { imageUrl, size } = localData;
    const { messageTemplateType } = localData;
    if (
      type !== customerMessageTemplateType &&
      localData.size.height !== 1040
    ) {
      size = { width: 1040, height: 1040 };
      imageUrl = '';
      setMaxHeight(size.height * (screenSize / size.width));
    }
    if (
      (messageTemplateType === customerMessageTemplateType &&
        type !== customerMessageTemplateType) ||
      (messageTemplateType !== customerMessageTemplateType &&
        type === customerMessageTemplateType)
    ) {
      setUploadImageError();
    }
    setLocalData({
      ...localData,
      imageUrl,
      size,
      messageTemplateType: type,
      aspectRatio: '1:1',
      buttons: ImagemapTemplateType[type].map((btn, index) => {
        if (localData.buttons[index]) {
          return {
            ...btn,
            title: localData.buttons[index].title,
            type: localData.buttons[index].type,
            data: localData.buttons[index].data,
            tags: localData.buttons[index].tags,
          };
        }
        return btn;
      }),
    });
  }

  function handleChangeActionType(type, index) {
    setLocalData({
      ...localData,
      buttons: localData.buttons.map((btn, actionIndex) => {
        if (index === actionIndex) {
          return {
            ...btn,
            title: '',
            type,
            data: '',
            tags: [],
          };
        }
        return btn;
      }),
    });
  }

  function handleChangeActionData(actionData, index) {
    setLocalData({
      ...localData,
      buttons: localData.buttons.map((btn, actionIndex) => {
        if (index === actionIndex) {
          return {
            ...btn,
            title: actionData,
            data: actionData,
          };
        }
        return btn;
      }),
    });
    setIsModified(true);
  }

  function handleChangeActionTags(tag, index) {
    setLocalData({
      ...localData,
      buttons: localData.buttons.map((btn, actionIndex) => {
        if (index === actionIndex) {
          return {
            ...btn,
            tags: [tag],
          };
        }
        return btn;
      }),
    });
  }

  function handleOpenFileChooser() {
    bgImageRef.current.click();
  }

  async function handleSelectFile() {
    const file = bgImageRef.current.files ? bgImageRef.current.files[0] : null;
    if (file) {
      const name = file?.name
        ? file.name.slice(0, file.name.lastIndexOf('.'))
        : '';
      const img = new Image();
      img.onload = async () => {
        bgImageRef.current.value = '';
        if (file.size > IMAGE_MAX_SIZE) {
          setUploadImageError(t('templates.imagemap-edit.upload-size'));
          return;
        }
        if (localData.messageTemplateType === customerMessageTemplateType) {
          if (img.width !== 1040 || img.height > 2080 || img.height < 520) {
            setUploadImageError(
              t('templates.imagemap-edit.upload-falied-rule2'),
            );
            return;
          }
        } else if (img.width !== 1040 || img.height !== 1040) {
          setUploadImageError(t('templates.imagemap-edit.upload-falied-rule'));
          return;
        }

        if (!(name && validateFileName(name))) {
          setUploadImageError(
            t('templates.imagemap-edit.upload-failed-filename'),
          );
          return;
        }
        setUploadImageError();

        try {
          setIsImageLoading(true);
          const response = await FileService.upload(file);
          const imageUrl = response.url;
          const newSize = { width: img.width, height: img.height };

          let { buttons } = localData;
          if (localData.size.height !== newSize.height) {
            buttons = ImagemapTemplateType[localData.messageTemplateType].map(
              (btn, index) => {
                if (localData.buttons[index]) {
                  return {
                    ...btn,
                    title: localData.buttons[index].title,
                    type: localData.buttons[index].type,
                    data: localData.buttons[index].data,
                    tags: localData.buttons[index].tags,
                  };
                }
                return btn;
              },
            );
          }
          setLocalData({
            ...localData,
            imageUrl,
            size: newSize,
            aspectRatio:
              newSize.width >= newSize.height
                ? `${newSize.width / newSize.height}:1`
                : `1:${newSize.height / newSize.width}`,
            buttons,
          });
          setMaxHeight(
            Math.floor(newSize.height * (screenSize / newSize.width)),
          );
        } catch (error) {
          const errorMessage = error.response
            ? error.response.data.error
              ? error.response.data.error.message
              : error.response.data.message
            : error.message;
          dispatch(
            enqueueSnackbar({
              variant: SnackBarType.ERROR,
              message: errorMessage,
            }),
          );
        } finally {
          setTimeout(() => {
            setIsImageLoading(false);
          }, 200);
        }
      };
      img.src = URL.createObjectURL(file);
    }
  }

  function handleSubmit() {
    onSubmit({
      ...localData,
      name:
        data.name ||
        `${t('types.message-template-type.imagemap')} ${moment().format(
          'YYYY-MM-DD HH:mm',
        )}`,
    });
  }

  const actionBoxComponents = localData.buttons.map((btn, i) => {
    return (
      <MenuContentBox
        setBounds={setBounds}
        removeAreaByIndex={removeAreaByIndex}
        key={`menuContentBox-${i}`}
        area={{
          bounds: {
            x: (parseInt(btn?.x, 10) / 100) * maxWidth,
            y: (parseInt(btn?.y, 10) / 100) * maxHeight,
            width: (parseInt(btn?.width, 10) / 100) * maxWidth,
            height: (parseInt(btn?.height, 10) / 100) * maxHeight,
          },
          action: {
            type: btn?.type,
            tags: btn?.tags,
            title: btn?.title,
            data: btn?.data,
          },
        }}
        index={i}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        minWidth={minWidth}
        minHeight={minHeight}
        isSelect={selectedContentIndex === i}
        onClick={() => {
          setSelectedContentIndex(i);
        }}
        disabledDelete={localData.buttons.length === 1}
      />
    );
  });

  // eslint-disable-next-line no-unused-vars
  function handleOnFocus(index) {
    if (index === localData.buttons.length - 1) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const contentHeight = contentRef.current.clientHeight;
        if (
          contentRef.current.scrollTop <
          contentRef.current.scrollHeight - contentHeight
        ) {
          scrollToWithAnimation(
            contentRef.current,
            contentRef.current.scrollHeight - contentHeight,
            200,
          );
        }
      }, 200);
    }
  }

  const actionSettingComponents = localData.buttons.map((btn, index) => {
    const tag = btn.tags ? btn.tags[0] : null;
    return (
      <div
        className={classes.actionSettingContainer}
        key={`action-setting-${index}`}
      >
        <label htmlFor={`action-setting-${index}`}>
          {t('templates.imagemap-edit.item', { number: index + 1 })}
        </label>
        <div className={classes.actionSetting} id={`action-setting-${index}`}>
          <Dropdown
            className={classes.dropdown}
            menuItems={dropdownList}
            value={btn.type}
            onChange={v => handleChangeActionType(v, index)}
            onFocus={() => handleOnFocus(index)}
          />
          {btn.type !== 'location' && (
            <>
              <TextFieldCardCounter
                id={`dataField-${index}`}
                className={classes['actionSetting-input']}
                placeholder={t(
                  `templates.imagemap-action-placeholder.${btn.type}`,
                )}
                value={btn.data}
                onChange={v => handleChangeActionData(v, index)}
                maxCount={btn.type === 'postback' ? 20 : 0}
                status={validAction(btn, isModified) ? undefined : 'error'}
                onFocus={() => handleOnFocus(index)}
              />

              <TextFieldCardCounter
                placeholder={t('templates.imagemap-edit.url-placeholder')}
                maxCount={0}
                value={tag}
                onChange={v => handleChangeActionTags(v, index)}
              />
            </>
          )}
        </div>
      </div>
    );
  });

  const contentComponent = (
    <div className={classes.root}>
      <div className={classes.messageTemplateContainer}>
        <h6 className={classes.title}>
          {t('templates.imagemap-edit.imagemap-template-title')}
          <a
            href={t('templates.imagemap-edit.helpUrl')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('templates.template-type-reference')}
          </a>
        </h6>
        <div className={classes.messageTemplate}>
          <div
            className={classes.messageTemplateItem}
            onClick={() => handleChangeMessageTemplateType('ImagemapTemplate1')}
          >
            <ImagemapTemplate1
              selected={localData.messageTemplateType === 'ImagemapTemplate1'}
            />
          </div>
          <div
            className={classes.messageTemplateItem}
            onClick={() => handleChangeMessageTemplateType('ImagemapTemplate2')}
          >
            <ImagemapTemplate2
              selected={localData.messageTemplateType === 'ImagemapTemplate2'}
            />
          </div>
          <div
            className={classes.messageTemplateItem}
            onClick={() => handleChangeMessageTemplateType('ImagemapTemplate3')}
          >
            <ImagemapTemplate3
              selected={localData.messageTemplateType === 'ImagemapTemplate3'}
            />
          </div>
          <div
            className={classes.messageTemplateItem}
            onClick={() => handleChangeMessageTemplateType('ImagemapTemplate4')}
          >
            <ImagemapTemplate4
              selected={localData.messageTemplateType === 'ImagemapTemplate4'}
            />
          </div>
          <div
            className={classes.messageTemplateItem}
            onClick={() =>
              handleChangeMessageTemplateType(customerMessageTemplateType)
            }
          >
            <ImagemapTemplate5
              selected={
                localData.messageTemplateType === customerMessageTemplateType
              }
            />
          </div>
        </div>
      </div>
      <div className={classes.bgImageContainer}>
        <h6 className={classes.title}>
          {t('templates.imagemap-edit.background-image-title')}
        </h6>
        <div className={classes.bgImageContent}>
          <div
            id="MenuContent-content"
            className={classnames(classes.bgImage, {
              [classes['bgImage--error']]: isModified && !localData.imageUrl,
            })}
            ref={imageRef}
            style={{ width: screenSize, height: maxHeight }}
            onMouseDown={onMouseDown}
          >
            {localData.imageUrl && (
              <img src={localData.imageUrl} alt="Background" />
            )}
            {actionBoxComponents}
            <div ref={newBoxRef} className={classes.newBox} />
          </div>
          <div className={classes.bgImageUpload}>
            {uploadImageError && (
              <p className={classes['bgImageUpload-errorMessage']}>
                {uploadImageError}
              </p>
            )}
            <p>
              {localData.messageTemplateType === customerMessageTemplateType
                ? t('templates.imagemap-edit.upload-rule2')
                : t('templates.imagemap-edit.upload-rule')}
            </p>
            <Button
              topic="primaryBlue"
              onClick={handleOpenFileChooser}
              isLoading={isImageLoading}
            >
              {t('templates.imagemap-edit.upload-button')}
            </Button>
            <input
              ref={bgImageRef}
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handleSelectFile}
            />
          </div>
        </div>
      </div>
      {actionSettingComponents}
    </div>
  );

  return (
    <DialogText
      isOpen
      title={t('templates.imagemap')}
      content={contentComponent}
      contentRef={contentRef}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      cancelText={t('modal.cancel')}
      submitText={t('modal.sure-2')}
      isSubmitDisabled={!validateCardData(localData, uploadImageError)}
    />
  );
}

ImagemapDialog.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
};

ImagemapDialog.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        data: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }).isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ImagemapDialog;
